import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  tableCellClasses,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import styled_ from '@emotion/styled';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCustomItemReport } from '../../store/reportStore/reportThunk';
import { CustomItemReportFetch, CustomItemReportRow } from '../../utils/report.types';
import { Utils } from '../../utils/common-utils';
import ReusableReportHeader from './ReusableReportHeader';
import LocationReportFilter from '../Inventory/pickers/LocationReportFilter';
import { useReportLocationId } from '../Inventory/pickers/useReportLocationId';

// Custom Item Report — user-picked columns (persisted locally) drive the table and the Excel/PDF exports.

const StyledTableCell = styled_(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
  },
}));

type ColumnKey = keyof CustomItemReportRow;
type ColumnKind = 'text' | 'qty' | 'money' | 'percent' | 'date' | 'bool' | 'status';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  kind: ColumnKind;
  align?: 'left' | 'right';
}

// Canonical order — the table always renders selected columns in this order.
const COLUMNS: ColumnDef[] = [
  { key: 'itemName', label: 'Item', kind: 'text' },
  { key: 'batchNumber', label: 'Batch', kind: 'text' },
  { key: 'expiryDate', label: 'Expiry', kind: 'date' },
  { key: 'itemCode', label: 'Item Code', kind: 'text' },
  { key: 'hsnCode', label: 'HSN Code', kind: 'text' },
  { key: 'description', label: 'Description', kind: 'text' },
  { key: 'categoryName', label: 'Category', kind: 'text' },
  { key: 'subCategoryName', label: 'Sub Category', kind: 'text' },
  { key: 'unitCode', label: 'Unit', kind: 'text' },
  { key: 'atLocation', label: 'At Location', kind: 'text' },
  { key: 'atLocationQty', label: 'Location Qty', kind: 'text', align: 'right' },
  { key: 'currentStock', label: 'Current Stock', kind: 'qty', align: 'right' },
  { key: 'stockStatus', label: 'Stock Status', kind: 'status' },
  { key: 'lowStockQuantity', label: 'Low Stock Qty', kind: 'qty', align: 'right' },
  { key: 'reorderQuantity', label: 'Reorder Qty', kind: 'qty', align: 'right' },
  { key: 'mrp', label: 'MRP', kind: 'money', align: 'right' },
  { key: 'salePrice', label: 'Sale Price', kind: 'money', align: 'right' },
  { key: 'taxInclusiveSalePrice', label: 'Sale Price (incl. GST)', kind: 'money', align: 'right' },
  { key: 'purchasePrice', label: 'Purchase Price', kind: 'money', align: 'right' },
  { key: 'wholesalePrice', label: 'Wholesale Price', kind: 'money', align: 'right' },
  { key: 'gstRate', label: 'GST %', kind: 'percent', align: 'right' },
  { key: 'stockValue', label: 'Stock Value (Purchase)', kind: 'money', align: 'right' },
  { key: 'saleValue', label: 'Stock Value (Sale)', kind: 'money', align: 'right' },
  { key: 'isBatchTracked', label: 'Batch Tracked', kind: 'bool' },
  { key: 'createdAt', label: 'Created On', kind: 'date' },
  { key: 'updatedAt', label: 'Last Updated', kind: 'date' },
];

const DEFAULT_COLUMNS: ColumnKey[] = ['itemName', 'batchNumber', 'expiryDate', 'itemCode', 'categoryName', 'unitCode', 'currentStock', 'stockStatus', 'salePrice'];
const STORAGE_KEY = 'customItemReport.columns';
// Columns added after users already saved a pick — shown once so batch rows aren't ambiguous.
const NEW_COLUMNS: ColumnKey[] = ['batchNumber', 'expiryDate'];
const SEEN_KEY = 'customItemReport.seenColumns';
// Always shown — an item report without the item name is meaningless.
const LOCKED_COLUMNS: ColumnKey[] = ['itemName'];
const withLocked = (keys: ColumnKey[]) => COLUMNS.map((c) => c.key).filter((k) => keys.includes(k) || LOCKED_COLUMNS.includes(k));
const SELECT_ALL = '__all__';
const RESET_DEFAULT = '__default__';

const loadSavedColumns = (): ColumnKey[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        let valid = parsed.filter((k) => COLUMNS.some((c) => c.key === k)) as ColumnKey[];
        const seen: string[] = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const unseen = NEW_COLUMNS.filter((k) => !seen.includes(k));
        if (unseen.length) {
          valid = COLUMNS.map((c) => c.key).filter((k) => valid.includes(k) || unseen.includes(k));
          localStorage.setItem(SEEN_KEY, JSON.stringify(NEW_COLUMNS));
        }
        if (valid.length) return withLocked(valid);
      }
    }
  } catch {
    /* localStorage unavailable — fall through to defaults */
  }
  return DEFAULT_COLUMNS;
};

const saveColumns = (keys: ColumnKey[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
};

const STATUS_COLORS: Record<string, string> = {
  'Out of Stock': '#d32f2f',
  'Low Stock': '#f57c00',
  'In Stock': '#2e7d32',
};

const fmtDate = (v: any) => (v ? new Date(String(v)).toLocaleDateString('en-GB') : '-');

// Plain-text value for exports (no JSX, no currency symbol quirks).
const cellText = (col: ColumnDef, row: CustomItemReportRow): string => {
  const v = row[col.key];
  switch (col.kind) {
    case 'money':
    case 'qty':
      return String(Number(v) || 0);
    case 'percent':
      return `${Number(v) || 0}%`;
    case 'date':
      return fmtDate(v);
    case 'bool':
      return v ? 'Yes' : 'No';
    default:
      return v === undefined || v === null || v === '' ? '-' : String(v);
  }
};

const esc = (s: any) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

// Borderless MUI Select styling shared by the other report filter bars.
const selectSx = {
  boxShadow: 'none',
  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
  '& .MuiSelect-select': { p: 0 },
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
};
const menuProps = { PaperProps: { sx: { border: '1.5px solid #e9ecf1', boxShadow: 'none', borderRadius: '5px', maxHeight: 420 } } };

const CustomItemReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state) => state.auth);

  const [rows, setRows] = useState<CustomItemReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedColumns, setSelectedColumns] = useState<ColumnKey[]>(loadSavedColumns);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(''); // debounced copy of searchInput
  const [locationId, setLocationId] = useReportLocationId(); // "" = All Locations
  const [stockFilter, setStockFilter] = useState('All');
  const [sortBy, setSortBy] = useState('itemName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    saveColumns(selectedColumns);
  }, [selectedColumns]);

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => selectedColumns.includes(c.key)), [selectedColumns]);

  const fetchData = useCallback(async () => {
    if (!selectedAccount?._id) return;
    setIsLoading(true);
    const params: CustomItemReportFetch = {
      accountId: selectedAccount._id,
      search: search || undefined,
      locationId: locationId || undefined,
      stockStatus: stockFilter === 'All' ? undefined : stockFilter,
      sortBy,
      sortOrder,
    };
    try {
      const response = await dispatch(fetchCustomItemReport(params)).unwrap();
      setRows(response?.data?.items || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch custom item report');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedAccount, search, locationId, stockFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnsChange = (value: string[]) => {
    if (value.includes(SELECT_ALL)) {
      setSelectedColumns(COLUMNS.map((c) => c.key));
      return;
    }
    if (value.includes(RESET_DEFAULT)) {
      setSelectedColumns(DEFAULT_COLUMNS);
      return;
    }
    setSelectedColumns(withLocked(value as ColumnKey[]));
  };

  const handleRefreshFilters = () => {
    setSearchInput('');
    setSearch('');
    setLocationId('');
    setStockFilter('All');
    setSortBy('itemName');
    setSortOrder('asc');
  };

  // -------- export (selected columns only, client-side) --------
  const buildTableHtml = () => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${visibleColumns.map((c) => `<th align="left">${esc(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${rows.map((r) => `<tr>${visibleColumns.map((c) => `<td>${esc(cellText(c, r))}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>`;

  const handleDownloadExcel = () => {
    const html = `<html><head><meta charset="utf-8" /></head><body>
      <h3>Custom Item Report</h3><div>${esc(selectedAccount?.legalName || selectedAccount?.name || '')} · ${rows.length} items · ${new Date().toLocaleDateString('en-GB')}</div><br/>
      ${buildTableHtml()}</body></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Custom_Item_Report_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Custom Item Report</title>
      <style>@page{size:A4 landscape;margin:10mm}
      body{font-family:Arial;padding:0} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:10px} th,td{border:0.75px solid #d9d4d4;padding:4px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Custom Item Report</h3><div class="sub">${esc(selectedAccount?.legalName || selectedAccount?.name || '')} · ${rows.length} items · ${new Date().toLocaleDateString('en-GB')}</div>
      ${buildTableHtml()}</body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;left:-9999px;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
  };

  const renderCell = (col: ColumnDef, row: CustomItemReportRow) => {
    const v = row[col.key];
    switch (col.kind) {
      case 'money':
        return Utils.formatNumber(Number(v) || 0, false, true);
      case 'qty':
        return Utils.formatNumber(Number(v) || 0, false, false);
      case 'percent':
        return `${Number(v) || 0}%`;
      case 'date':
        return fmtDate(v);
      case 'bool':
        return v ? 'Yes' : 'No';
      case 'status':
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '9pt', color: STATUS_COLORS[String(v)] || '#292d35' }}>
            {String(v || '-')}
          </Typography>
        );
      default:
        return v === undefined || v === null || v === '' ? '-' : String(v);
    }
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      <ReusableReportHeader
        reportType="CUSTOM_ITEM_REPORT"
        title="Custom Item Report"
        subtitle="Choose the columns you want to see — stock, prices, GST, location-wise"
        recordCount={rows.length}
        isLoading={isLoading}
        onDownloadPDF={handleDownloadPDF}
        onDownloadExcel={handleDownloadExcel}
        showPrint={false}
        showShare={false}
      />

      <div style={{ marginBottom: 12 }} id="pdfContainer">
        {/* Filters strip */}
        <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7', marginBottom: 0 }}>
          <Grid xs={12} style={{ textAlign: 'left' }}>
            <Box style={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '10px' }}>
              <IconButton
                onClick={handleRefreshFilters}
                style={{ marginRight: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
                size="small"
              >
                <RefreshIcon />
              </IconButton>

              <TextField
                className="c-text-field-3"
                variant="standard"
                size="small"
                placeholder="Search item / code / HSN ..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ width: 260 }}
                InputProps={{ disableUnderline: true }}
              />

              {/* Location filter — hidden automatically for single-location accounts */}
              <LocationReportFilter borderless width={180} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" />

              <Select
                name="stockFilter"
                disableUnderline
                MenuProps={menuProps}
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                sx={{ ...selectSx, width: 150 }}
                className="c-text-field-3"
                variant="outlined"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="In Stock">In Stock</MenuItem>
                <MenuItem value="Low Stock">Low Stock</MenuItem>
                <MenuItem value="Out of Stock">Out of Stock</MenuItem>
              </Select>

              <Select
                name="sort"
                disableUnderline
                MenuProps={menuProps}
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                sx={{ ...selectSx, width: 200 }}
                className="c-text-field-3"
                variant="outlined"
              >
                <MenuItem value="itemName-asc">Name: A to Z</MenuItem>
                <MenuItem value="itemName-desc">Name: Z to A</MenuItem>
                <MenuItem value="currentStock-asc">Stock: Low to High</MenuItem>
                <MenuItem value="currentStock-desc">Stock: High to Low</MenuItem>
                <MenuItem value="salePrice-asc">Sale Price: Low to High</MenuItem>
                <MenuItem value="salePrice-desc">Sale Price: High to Low</MenuItem>
                <MenuItem value="purchasePrice-asc">Purchase Price: Low to High</MenuItem>
                <MenuItem value="purchasePrice-desc">Purchase Price: High to Low</MenuItem>
                <MenuItem value="stockValue-desc">Stock Value: High to Low</MenuItem>
              </Select>

              {/* Column picker — what the user ticks here is exactly what the table and exports show */}
              <Select
                multiple
                disableUnderline
                value={selectedColumns}
                onChange={(e) => handleColumnsChange(e.target.value as string[])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label={`Columns: ${(selected as string[]).length} / ${COLUMNS.length}`} size="small" sx={{ height: '20px' }} />
                  </Box>
                )}
                MenuProps={menuProps}
                sx={{ ...selectSx, width: 200 }}
                className="c-text-field-3"
                variant="outlined"
              >
                <MenuItem value={SELECT_ALL} sx={{ fontWeight: 600, color: '#488dff' }}>Select all</MenuItem>
                <MenuItem value={RESET_DEFAULT} sx={{ fontWeight: 600, color: '#7a8290' }}>Reset to default</MenuItem>
                <Divider />
                {COLUMNS.map((c) => (
                  <MenuItem key={c.key} value={c.key} dense disabled={LOCKED_COLUMNS.includes(c.key)}>
                    <Checkbox size="small" checked={selectedColumns.includes(c.key)} />
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
        </Grid>

        {/* Data table — only the selected columns, in canonical order */}
        <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size="small" aria-label="custom item report">
            <TableHead>
              <TableRow>
                {visibleColumns.map((c) => (
                  <StyledTableCell key={c.key} align={c.align || 'left'}>{c.label}</StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                  </StyledTableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row._id}>
                    {visibleColumns.map((c) => (
                      <StyledTableCell key={c.key} align={c.align || 'left'}>{renderCell(c, row)}</StyledTableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">No items match the selected filters</Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Box>
  );
};

export default CustomItemReport;
